import {
  getFormActions,
  invalidateFormActionsCache,
  FormAction,
  FormActionStatus,
  CustomSchemaAction,
  DetectedConsent,
  CONSENT_CHANNEL_LABELS,
  NATIVE_FORM_ACTIONS,
  PLATFORMS_WITHOUT_PASSWORD,
} from './FormActionsConfig';
import {
  getFormFieldsSummary,
  TemplateEditorState as state,
  getFormFieldConfig,
  getFormByIdentifier,
  updateFormFieldConfig,
  hasDisclosureWidgetInTemplate,
  countConsentCheckboxesInTemplate,
} from './TemplateEditor';
import { UserState } from '@/composables/User';
import { i18n } from '@/i18n';

/**
 * Interface for inherited funnel data
 */
interface FunnelInheritedData {
  activatedActions: string[];
  nativeProperties: Set<string>;
  customProperties: Array<{ property: string; sourceTemplate: string }>;
  inheritedConsentChannels: Set<string>;
}

/**
 * Get disclosure channels configured on the disclosure widget in the current template
 */
const getDisclosureChannelsFromTemplate = (): Set<string> => {
  const channels = new Set<string>();
  if (!hasDisclosureWidgetInTemplate() || !state.template?.configuration?.forms) {
    return channels;
  }
  // Check for disclosure widget in form configs
  for (const formConfig of Object.values(state.template.configuration.forms) as any[]) {
    if (formConfig.type === 'widget-disclosure') {
      const configuredChannels = formConfig.configs?.['data-disclosure-channels'];
      if (typeof configuredChannels === 'string' && configuredChannels) {
        configuredChannels.split(',').filter(Boolean).forEach((ch: string) => channels.add(ch));
      }
    }
  }
  return channels;
};

/**
 * Check if account_creation is active in the current template or inherited from funnel parents
 */
const isAccountCreationActiveInFunnel = (
  currentTemplateActions: string[],
  inheritedData: FunnelInheritedData,
): boolean => {
  if (currentTemplateActions.includes('account_creation')) return true;
  return inheritedData.activatedActions.includes('account_creation');
};

/**
 * Evaluate consent channels for the consent action
 */
const evaluateConsentChannels = (
  action: FormAction,
  availableProperties: Set<string>,
  inheritedConsentChannels?: Set<string>,
  hasAccountCreation?: boolean,
): DetectedConsent[] => {
  if (!action.consentChannels) return [];

  const disclosureChannels = getDisclosureChannelsFromTemplate();

  return action.consentChannels.map((channel) => {
    // Channel already collected in a previous funnel template
    if (inheritedConsentChannels?.has(channel.id)) {
      return {
        channelId: channel.id,
        label: CONSENT_CHANNEL_LABELS[channel.id] || channel.id,
        mode: 'already_collected' as DetectedConsent['mode'],
        identifierPresent: true,
        consentFieldPresent: false,
        hasDisclosure: false,
        disclosureRequired: false,
        checkboxRequiredByAccountCreation: false,
        disclosureAdvisory: false,
      };
    }

    const identifierPresent = availableProperties.has(channel.identifierField);
    const consentFieldPresent = availableProperties.has(channel.consentField);
    const disclosureCoversChannel = disclosureChannels.has(channel.id);

    let mode: DetectedConsent['mode'];
    let checkboxRequiredByAccountCreation = false;

    if (!identifierPresent) {
      mode = 'not_applicable';
    } else if (consentFieldPresent) {
      mode = 'explicit';
    } else if (channel.checkboxAlwaysRequired) {
      mode = 'not_configured';
    } else if (channel.implicitAllowed) {
      if (hasAccountCreation) {
        mode = 'implicit';
        checkboxRequiredByAccountCreation = true;
      } else {
        mode = 'implicit';
      }
    } else {
      mode = 'not_configured';
    }

    const isMarketingActive = mode === 'explicit' || mode === 'implicit';
    const disclosureAdvisory = isMarketingActive && !disclosureCoversChannel;

    return {
      channelId: channel.id,
      label: CONSENT_CHANNEL_LABELS[channel.id] || channel.id,
      mode,
      identifierPresent,
      consentFieldPresent,
      hasDisclosure: disclosureCoversChannel,
      disclosureRequired: false,
      checkboxRequiredByAccountCreation,
      disclosureAdvisory,
    };
  });
};

/**
 * Evaluate a single action against available properties
 * @param action - The action to evaluate
 * @param availableProperties - Set of available properties
 * @param customProperties - Array of custom properties
 * @returns Action status
 */
const evaluateAction = (
  action: FormAction,
  availableProperties: Set<string>,
  customProperties: string[],
  inheritedConsentChannels?: Set<string>,
  hasAccountCreation?: boolean,
): FormActionStatus => {
  const missingFields: string[] = [];
  const availableFields: string[] = [];
  const optionalAvailableFields: string[] = [];

  // Special handling for consent action
  if (action.id === 'consent') {
    const detectedConsents = evaluateConsentChannels(action, availableProperties, inheritedConsentChannels, hasAccountCreation);
    const hasActiveChannel = detectedConsents.some((c) => c.mode === 'explicit' || c.mode === 'implicit');
    const hasIdentifier = (action.identificationFields || []).some((f) => availableProperties.has(f));

    // Track which identification fields are present
    (action.identificationFields || []).forEach((field) => {
      if (availableProperties.has(field)) {
        availableFields.push(field);
      }
    });

    return {
      action,
      isAvailable: hasIdentifier,
      isEnabled: hasIdentifier,
      missingFields: hasIdentifier ? [] : ['email', 'phone_number'],
      availableFields,
      optionalAvailableFields: [],
      detectedConsents,
    };
  }

  // Resolve conditional required fields based on platform
  const resolvedRequiredFields = [...action.requiredFields];
  if (action.conditionalRequiredFields) {
    const shopPlatform = UserState.activeShop?.solutionEcommerce?.toLowerCase() || '';
    action.conditionalRequiredFields.forEach((crf) => {
      if (crf.condition === 'platformRequiresPassword') {
        if (!PLATFORMS_WITHOUT_PASSWORD.includes(shopPlatform)) {
          resolvedRequiredFields.push(crf.field);
        }
      }
    });
  }

  // Check required fields
  resolvedRequiredFields.forEach((field) => {
    if (availableProperties.has(field)) {
      availableFields.push(field);
    } else {
      missingFields.push(field);
    }
  });

  // Check optional fields
  action.optionalFields.forEach((field) => {
    if (availableProperties.has(field)) {
      optionalAvailableFields.push(field);
    }
  });

  const isAvailable = missingFields.length === 0;

  return {
    action,
    isAvailable,
    isEnabled: isAvailable,
    missingFields,
    availableFields,
    optionalAvailableFields,
  };
};

/**
 * Check if a property is a native contact property
 * @param property - Property name to check
 * @returns boolean indicating if property is native
 */
const isNativeProperty = (property: string): boolean => {
  const nativeProperties = ['email', 'first_name', 'last_name', 'phone_number', 'birthday', 'consent_email', 'consent_sms', 'consent_whatsapp'];
  return nativeProperties.includes(property);
};

/**
 * Check if current template is a redirection template (has origin_template_id)
 * @returns boolean indicating if template is a redirection template
 */
const isRedirectionTemplate = (): boolean => (
  !!(state.template?.configuration?.origin_template_id && state.template?.configuration?.root_template_id)
);

/**
 * Get template from funnel by ID
 * @param templateId - ID of the template to find
 * @returns Template object or null if not found
 */
const getTemplateFromFunnel = (templateId: number): Record<string, any> | null => {
  if (!state.template?.originTemplateId || !state.templatesFromFunnel[state.template.originTemplateId]) {
    return null;
  }
  return state.templatesFromFunnel[state.template.originTemplateId].find((t: Record<string, any>) => t.id === templateId) || null;
};

/**
 * Find the source template name for a native property by traversing the funnel
 * @param property - The native property to find the source for
 * @returns Template name or null if not found
 */
const findNativePropertySourceTemplate = (property: string): string | null => {
  if (!state.template?.configuration?.origin_template_id) {
    return null;
  }

  const rootTemplateId = parseInt(state.template.configuration.root_template_id as string, 10);
  let currentTemplateId = parseInt(state.template.configuration.origin_template_id as string, 10);
  const visitedTemplates = new Set<number>();

  // Traverse from current template back to root
  while (currentTemplateId && currentTemplateId !== rootTemplateId && !visitedTemplates.has(currentTemplateId)) {
    visitedTemplates.add(currentTemplateId);
    const currentTemplate = getTemplateFromFunnel(currentTemplateId);

    if (!currentTemplate) {
      break;
    }

    // Check if this template has the property
    if (currentTemplate.configuration?.forms) {
      const hasProperty = Object.values(currentTemplate.configuration.forms).some((formConfig: any) => {
        const contactProperty = formConfig.configs?.contactProperty || formConfig.contactProperty;
        return contactProperty === property || (property === 'password' && formConfig.type === 'INPUT_PASSWORD');
      });

      if (hasProperty) {
        return currentTemplate.informations?.name || `Template ${currentTemplateId}`;
      }
    }

    // Move to the next template in the chain
    if (currentTemplate.configuration?.origin_template_id) {
      currentTemplateId = parseInt(currentTemplate.configuration.origin_template_id as string, 10);
    } else {
      break;
    }
  }

  // Also check root template if we haven't reached it yet
  if (currentTemplateId === rootTemplateId && !visitedTemplates.has(rootTemplateId)) {
    const rootTemplate = getTemplateFromFunnel(rootTemplateId);
    if (rootTemplate?.configuration?.forms) {
      const hasProperty = Object.values(rootTemplate.configuration.forms).some((formConfig: any) => {
        const contactProperty = formConfig.configs?.contactProperty || formConfig.contactProperty;
        return contactProperty === property || (property === 'password' && formConfig.type === 'INPUT_PASSWORD');
      });

      if (hasProperty) {
        return rootTemplate.informations?.name || `Template ${rootTemplateId}`;
      }
    }
  }

  return null;
};

/**
 * Get properties collected by a specific action in the funnel
 * @param actionId - The action ID to check (e.g., 'account_creation', 'newsletter_subscription')
 * @returns Set of native properties collected when the action was activated
 */
const getPropertiesCollectedByAction = (actionId: string): Set<string> => {
  const collectedProperties = new Set<string>();

  if (!isRedirectionTemplate() || !state.template) {
    return collectedProperties;
  }

  const rootTemplateId = parseInt(state.template.configuration.root_template_id as string, 10);
  let currentTemplateId = parseInt(state.template.configuration.origin_template_id as string, 10);
  const visitedTemplates = new Set<number>();

  // Helper function to check if a template has the specified action activated
  const hasActionActivated = (template: Record<string, any>): boolean => {
    if (!template.configuration?.forms) {
      return false;
    }
    return (Object.values(template.configuration.forms) as Record<string, any>[]).some((formConfig) => {
      const actionsToTake = formConfig.configs?.actions_to_take || [];
      return actionsToTake.includes(actionId);
    });
  };

  // Helper function to collect properties from a template
  const collectPropertiesFromTemplate = (template: Record<string, any>) => {
    if (!template.configuration?.forms) {
      return;
    }
    (Object.values(template.configuration.forms) as Record<string, any>[]).forEach((formConfig) => {
      const contactProperty = formConfig.configs?.contactProperty || formConfig.contactProperty;
      if (contactProperty && contactProperty.trim() !== '') {
        if (formConfig.type === 'INPUT_PASSWORD') {
          collectedProperties.add('password');
        } else if (isNativeProperty(contactProperty)) {
          collectedProperties.add(contactProperty);
        }
      }
    });
  };

  // Traverse from current template back to root
  while (currentTemplateId && currentTemplateId !== rootTemplateId && !visitedTemplates.has(currentTemplateId)) {
    visitedTemplates.add(currentTemplateId);
    const currentTemplate = getTemplateFromFunnel(currentTemplateId);

    if (!currentTemplate) {
      break;
    }

    // If this template has the action activated, collect its properties
    if (hasActionActivated(currentTemplate)) {
      collectPropertiesFromTemplate(currentTemplate);
      // Stop here as we found the template where the action was activated
      return collectedProperties;
    }

    // Move to the next template in the chain
    if (currentTemplate.configuration?.origin_template_id) {
      currentTemplateId = parseInt(currentTemplate.configuration.origin_template_id as string, 10);
    } else {
      break;
    }
  }

  // Also check root template if we haven't reached it yet
  if (currentTemplateId === rootTemplateId && !visitedTemplates.has(rootTemplateId)) {
    const rootTemplate = getTemplateFromFunnel(rootTemplateId);
    if (rootTemplate && hasActionActivated(rootTemplate)) {
      collectPropertiesFromTemplate(rootTemplate);
    }
  }

  return collectedProperties;
};

/**
 * Determine which consent channels are active in a template's form configuration.
 * A channel is active if its identifier field is present AND either the consent field
 * is present (explicit) or implicit consent is allowed for that channel.
 */
const collectConsentChannelsFromTemplate = (templateForms: Record<string, any>): Set<string> => {
  const channels = new Set<string>();
  const consentAction = NATIVE_FORM_ACTIONS.find((a) => a.id === 'consent');
  if (!consentAction?.consentChannels) return channels;

  // Collect all native properties and disclosure channels from the template's forms
  const templateProperties = new Set<string>();
  const templateDisclosureChannels = new Set<string>();
  Object.values(templateForms).forEach((formConfig: any) => {
    const contactProperty = formConfig.configs?.contactProperty || formConfig.contactProperty;
    if (contactProperty && contactProperty.trim() !== '') {
      templateProperties.add(contactProperty);
    }
    // Check for disclosure widget channels
    if (formConfig.type === 'widget-disclosure') {
      const configuredChannels = formConfig.configs?.['data-disclosure-channels'];
      if (typeof configuredChannels === 'string' && configuredChannels) {
        configuredChannels.split(',').filter(Boolean).forEach((ch: string) => templateDisclosureChannels.add(ch));
      }
    }
  });

  // Evaluate each consent channel against the template's properties
  consentAction.consentChannels.forEach((channel) => {
    const identifierPresent = templateProperties.has(channel.identifierField);
    const consentFieldPresent = templateProperties.has(channel.consentField);

    // A channel is active if: explicit checkbox OR implicit allowed with identifier
    if (identifierPresent && (consentFieldPresent || channel.implicitAllowed)) {
      channels.add(channel.id);
    }
  });

  return channels;
};

/**
 * Traverse funnel from current template to root and collect inherited data
 * @returns FunnelInheritedData with accumulated actions and properties
 */
const getFunnelInheritedData = (): FunnelInheritedData => {
  const inheritedData: FunnelInheritedData = {
    activatedActions: [],
    nativeProperties: new Set<string>(),
    customProperties: [],
    inheritedConsentChannels: new Set<string>(),
  };

  if (!isRedirectionTemplate() || !state.template) {
    return inheritedData;
  }

  const rootTemplateId = parseInt(state.template.configuration.root_template_id as string, 10);
  let currentTemplateId = parseInt(state.template.configuration.origin_template_id as string, 10);
  const visitedTemplates = new Set<number>();

  // Traverse from current template back to root
  while (currentTemplateId && currentTemplateId !== rootTemplateId && !visitedTemplates.has(currentTemplateId)) {
    visitedTemplates.add(currentTemplateId);
    const currentTemplate = getTemplateFromFunnel(currentTemplateId);

    if (!currentTemplate) {
      break;
    }

    // Collect activated actions from this template
    let templateHasConsent = false;
    if (currentTemplate.configuration?.forms) {
      (Object.values(currentTemplate.configuration.forms) as Record<string, any>[]).forEach((formConfig) => {
        if (formConfig.configs?.actions_to_take) {
          formConfig.configs.actions_to_take.forEach((actionId: string) => {
            if (!inheritedData.activatedActions.includes(actionId)) {
              inheritedData.activatedActions.push(actionId);
            }
            if (actionId === 'consent' || actionId === 'newsletter_subscription') {
              templateHasConsent = true;
            }
          });
        }
      });
    }

    // Collect consent channels if this template has consent activated
    if (templateHasConsent && currentTemplate.configuration?.forms) {
      const channels = collectConsentChannelsFromTemplate(currentTemplate.configuration.forms);
      channels.forEach((ch) => inheritedData.inheritedConsentChannels.add(ch));
    }

    // Collect properties from this template's form fields
    if (currentTemplate.configuration?.forms) {
      const templateId = currentTemplateId; // Capture the value to avoid closure issues
      (Object.values(currentTemplate.configuration.forms) as Record<string, any>[]).forEach((formConfig) => {
        // Get contactProperty from configs (new structure) or fallback to old structure
        const contactProperty = formConfig.configs?.contactProperty || formConfig.contactProperty;
        if (contactProperty) {
          if (formConfig.type === 'INPUT_PASSWORD') {
            inheritedData.nativeProperties.add('password');
          } else if (isNativeProperty(contactProperty)) {
            inheritedData.nativeProperties.add(contactProperty);
          } else {
            // Custom property - only inherit if custom_data_storage not activated in this template
            const hasCustomDataStorage = formConfig.configs?.actions_to_take?.some(
              (a: string) => a.startsWith('custom_data_storage_'),
            );
            if (!hasCustomDataStorage && !inheritedData.customProperties.some((cp) => cp && cp.property === contactProperty)) {
              const templateName = currentTemplate.informations?.name || `Template ${templateId}`;
              inheritedData.customProperties.push({
                property: contactProperty,
                sourceTemplate: templateName,
              });
            }
          }
        }
      });
    }

    // Move to the next template in the chain
    if (currentTemplate.configuration?.origin_template_id) {
      currentTemplateId = parseInt(currentTemplate.configuration.origin_template_id as string, 10);
    } else {
      break;
    }
  }

  // Also check root template if we haven't reached it yet
  if (currentTemplateId === rootTemplateId && !visitedTemplates.has(rootTemplateId)) {
    const rootTemplate = getTemplateFromFunnel(rootTemplateId);
    if (rootTemplate?.configuration?.forms) {
      let rootHasConsent = false;
      (Object.values(rootTemplate.configuration.forms) as Record<string, any>[]).forEach((formConfig) => {
        if (formConfig.configs?.actions_to_take) {
          formConfig.configs.actions_to_take.forEach((actionId: string) => {
            if (!inheritedData.activatedActions.includes(actionId)) {
              inheritedData.activatedActions.push(actionId);
            }
            if (actionId === 'consent' || actionId === 'newsletter_subscription') {
              rootHasConsent = true;
            }
          });
        }
        // Get contactProperty from configs (new structure) or fallback to old structure
        const contactProperty = formConfig.configs?.contactProperty || formConfig.contactProperty;
        // Ignore fields with empty contactProperty
        if (contactProperty && contactProperty.trim() !== '') {
          if (formConfig.type === 'INPUT_PASSWORD') {
            inheritedData.nativeProperties.add('password');
          } else if (isNativeProperty(contactProperty)) {
            inheritedData.nativeProperties.add(contactProperty);
          } else {
            const hasCustomDataStorage = formConfig.configs?.actions_to_take?.some(
              (a: string) => a.startsWith('custom_data_storage_'),
            );
            if (!hasCustomDataStorage && !inheritedData.customProperties.some((cp) => cp && cp.property === contactProperty)) {
              const templateName = rootTemplate.informations?.name || `Template ${rootTemplateId}`;
              inheritedData.customProperties.push({
                property: contactProperty,
                sourceTemplate: templateName,
              });
            }
          }
        }
      });

      // Collect consent channels from root template
      if (rootHasConsent) {
        const channels = collectConsentChannelsFromTemplate(rootTemplate.configuration.forms);
        channels.forEach((ch) => inheritedData.inheritedConsentChannels.add(ch));
      }
    }
  }

  return inheritedData;
};

/**
 * Get default enabled actions for a new widget configuration
 * @returns Array of action IDs that should be enabled by default
 */
export const getDefaultEnabledActions = async (): Promise<string[]> => {
  invalidateFormActionsCache();

  const formFields = getFormFieldsSummary();
  const inheritedData = getFunnelInheritedData();
  const allActions = await getFormActions();

  // Get all available properties from the current form
  const availableProperties = new Set<string>();
  const customPropertiesBySchema: Record<string, string[]> = {};

  // Add inherited properties
  inheritedData.nativeProperties.forEach((prop) => availableProperties.add(prop));

  // Group custom properties by schema
  inheritedData.customProperties.forEach((cp) => {
    const match = cp.property.match(/^(\d+)_(.+)$/);
    if (match) {
      const [, schemaId, propertyName] = match;
      if (!customPropertiesBySchema[schemaId]) {
        customPropertiesBySchema[schemaId] = [];
      }
      customPropertiesBySchema[schemaId].push(propertyName);
    }
  });

  formFields.forEach((field) => {
    if (field.isPasswordField) {
      availableProperties.add('password');
    } else if (field.propertyType === 'custom' && field.contactProperty) {
      const match = field.contactProperty.match(/^(\d+)_(.+)$/);
      if (match) {
        const [, schemaId, propertyName] = match;
        if (!customPropertiesBySchema[schemaId]) {
          customPropertiesBySchema[schemaId] = [];
        }
        if (!customPropertiesBySchema[schemaId].includes(propertyName)) {
          customPropertiesBySchema[schemaId].push(propertyName);
        }
      }
    } else if (field.contactProperty) {
      availableProperties.add(field.contactProperty);
    }
  });

  // Return actions that are available and should be auto-activated
  return allActions
    .filter((action) => {
      // Consent action: available as long as there are uncollected channels
      if (action.id === 'consent') {
        const allChannelIds = (action.consentChannels || []).map((c) => c.id);
        const remainingChannels = allChannelIds.filter((c) => !inheritedData.inheritedConsentChannels.has(c));
        if (remainingChannels.length === 0) {
          return false;
        }
        if (action.autoActivate === false) {
          return false;
        }
        const hasIdentifier = (action.identificationFields || []).some((f) => availableProperties.has(f));
        return hasIdentifier;
      }

      // Exclude actions already activated in the funnel
      if (inheritedData.activatedActions.includes(action.id)) {
        return false;
      }

      // Only auto-activate actions that have autoActivate flag
      if (action.autoActivate === false) {
        return false;
      }

      // Handle custom schema actions
      if (action.id.startsWith('custom_data_storage_')) {
        const { schemaId } = action as CustomSchemaAction;
        const customAction = action as CustomSchemaAction;
        const availablePropsForSchema = customPropertiesBySchema[schemaId] || [];

        const missingFields = customAction.requiredFields.filter((reqField) => {
          const propertyName = reqField.replace(`${schemaId}_`, '');
          return !availablePropsForSchema.includes(propertyName);
        });

        return missingFields.length === 0 && availablePropsForSchema.length > 0;
      }

      // Handle native actions
      const missingFields = action.requiredFields.filter(
        (field) => !availableProperties.has(field),
      );
      return missingFields.length === 0;
    })
    .map((action) => action.id);
};

/**
 * Evaluate which form actions are available based on the current template fields
 * @param identifier - Widget identifier to restore action states from
 * @returns Array of form action statuses
 */
export const evaluateFormActions = async (identifier?: string): Promise<FormActionStatus[]> => {
  // Invalidate cache to ensure fresh custom schema data (schemas may have been created/deleted)
  invalidateFormActionsCache();

  const formFields = getFormFieldsSummary();
  const inheritedData = getFunnelInheritedData();
  const rawSavedActions: string[] = identifier ? (getFormFieldConfig(identifier, 'actions_to_take') || []) : [];
  const userDisabledActions: string[] = identifier ? (getFormFieldConfig(identifier, 'user_disabled_actions') || []) : [];
  const hasExistingConfig = !!(identifier && getFormByIdentifier(identifier));
  const userDisabledSet = new Set(userDisabledActions);

  // Migration: replace newsletter_subscription with consent in saved actions
  const migratedActions = rawSavedActions.map((actionId) => (actionId === 'newsletter_subscription' ? 'consent' : actionId));

  // Update state if migration changed the actions
  if (
    identifier
    && hasExistingConfig
    && migratedActions.some((value, index) => value !== rawSavedActions[index])
  ) {
    updateFormFieldConfig(identifier, 'actions_to_take', migratedActions);
  }

  const savedActionsSet = new Set(migratedActions);

  // Determine if account_creation is active in the funnel
  const currentTemplateActions = [...savedActionsSet];
  const hasAccountCreation = isAccountCreationActiveInFunnel(currentTemplateActions, inheritedData);

  // Get all available properties from the current form
  const availableProperties = new Set<string>();
  const customProperties: string[] = [];

  // Track inherited native properties for display
  const inheritedNativeProperties = new Map<string, string>();
  // Add inherited properties and track their source templates
  inheritedData.nativeProperties.forEach((prop) => {
    availableProperties.add(prop);
    // Find the source template by traversing the funnel to see where this property comes from
    const sourceTemplate = findNativePropertySourceTemplate(prop);
    if (sourceTemplate) {
      inheritedNativeProperties.set(prop, sourceTemplate);
    }
  });
  customProperties.push(...inheritedData.customProperties.map((cp) => cp.property));

  formFields.forEach((field) => {
    if (field.isPasswordField) {
      // Password fields are automatically considered for account creation
      availableProperties.add('password');
    } else if (field.propertyType === 'custom' && field.contactProperty && field.contactProperty.trim() !== '') {
      customProperties.push(field.contactProperty);
    } else if (field.contactProperty && field.contactProperty.trim() !== '') {
      availableProperties.add(field.contactProperty);
    }
  });

  // Group custom properties by schema ID and track inheritance
  const customPropertiesBySchema: Record<string, Array<{ property: string; isInherited: boolean; sourceTemplate?: string }>> = {};

  // First, add inherited properties
  inheritedData.customProperties.forEach((customProp) => {
    if (customProp && customProp.property) {
      const match = customProp.property.match(/^(\d+)_(.+)$/);
      if (match) {
        const [, schemaId, propertyName] = match;
        if (!customPropertiesBySchema[schemaId]) {
          customPropertiesBySchema[schemaId] = [];
        }
        customPropertiesBySchema[schemaId].push({
          property: propertyName,
          isInherited: true,
          sourceTemplate: customProp.sourceTemplate || 'Modèle précédent',
        });
      }
    }
  });

  // Then, add current form properties
  formFields.forEach((field) => {
    if (field.propertyType === 'custom' && field.contactProperty && field.contactProperty.trim() !== '') {
      const match = field.contactProperty.match(/^(\d+)_(.+)$/);
      if (match) {
        const [, schemaId, propertyName] = match;
        if (!customPropertiesBySchema[schemaId]) {
          customPropertiesBySchema[schemaId] = [];
        }
        // Check if property already exists (inherited)
        const existingProperty = customPropertiesBySchema[schemaId].find((p) => p.property === propertyName);
        if (!existingProperty) {
          customPropertiesBySchema[schemaId].push({
            property: propertyName,
            isInherited: false,
          });
        }
      }
    }
  });

  // Get all available actions (native + custom schemas)
  const allActions = await getFormActions();

  // Filter out actions already activated in funnel
  const availableActions = allActions.filter((action) => {
    // Consent action: always show (inherited channels displayed as grey)
    if (action.id === 'consent') {
      return true;
    }

    // Handle custom schema actions
    if (action.id.startsWith('custom_data_storage_')) {
      return !inheritedData.activatedActions.includes(action.id);
    }

    // Exclude actions already activated in previous templates
    return !inheritedData.activatedActions.includes(action.id);
  });

  // Evaluate each available action
  const result = availableActions
    .map((action: FormAction) => {
      const actionStatus = evaluateAction(action, availableProperties, customProperties, inheritedData.inheritedConsentChannels, hasAccountCreation);

      // Add custom properties grouped by schema for custom schema actions
      if (action.id.startsWith('custom_data_storage_')) {
        const customAction = action as CustomSchemaAction;
        const { schemaId } = customAction;
        const availablePropsForSchema = (customPropertiesBySchema[schemaId] || []).map((p) => ({
          ...p,
          label: customAction.fieldLabels?.[p.property] || undefined,
        }));

        const missingRequiredFields = customAction.requiredFields.filter((reqField) => {
          const propertyName = reqField.replace(`${schemaId}_`, '');
          return !availablePropsForSchema.some((p) => p.property === propertyName);
        });

        actionStatus.isAvailable = missingRequiredFields.length === 0 && availablePropsForSchema.length > 0;
        actionStatus.missingFields = missingRequiredFields.map((field) => field.replace(`${schemaId}_`, ''));
        actionStatus.customPropertiesBySchema = {
          [schemaId]: availablePropsForSchema,
        };
      }

      // Add inheritance information for native properties
      actionStatus.inheritedNativeProperties = inheritedNativeProperties;

      // Determine enabled state: auto-enable when available, force-disable when not, respect user toggle-off
      if (action.id === 'consent') {
        // Consent enabled when there's at least one active channel:
        // - marketing: explicit consent field present (non-inherited)
        // - transactional: identifier (email/phone) present (non-inherited)
        const hasActiveChannel = actionStatus.detectedConsents?.some(
          (c) => c.mode !== 'already_collected' && c.identifierPresent,
        );
        actionStatus.isEnabled = !!hasActiveChannel;
      } else if (hasExistingConfig) {
        if (!actionStatus.isAvailable) {
          actionStatus.isEnabled = false;
        } else if (userDisabledSet.has(action.id)) {
          actionStatus.isEnabled = false;
        } else {
          actionStatus.isEnabled = true;
        }
      } else {
        if (action.autoActivate === false) {
          actionStatus.isEnabled = false;
        } else {
          actionStatus.isEnabled = actionStatus.isAvailable;
        }
      }

      return actionStatus;
    })
    .filter((actionStatus: FormActionStatus) => {
      // Filter out custom schema actions with no properties
      if (actionStatus.action.id.startsWith('custom_data_storage_')) {
        const { schemaId } = actionStatus.action as CustomSchemaAction;
        const hasProperties = customPropertiesBySchema[schemaId] && customPropertiesBySchema[schemaId].length > 0;
        return hasProperties;
      }
      return true;
    })
    .sort((a: FormActionStatus, b: FormActionStatus) => a.action.priority - b.action.priority);

  // --- Consentement au suivi des ouvertures (implicite, niveau bouton) ---
  // Proposé uniquement si un email est collecté ET qu'aucun widget case de
  // consentement n'est présent (sinon c'est la case qui gère le suivi). Défaut ON.
  const consentStatusForTracking = result.find((s) => s.action.id === 'consent');
  if (consentStatusForTracking) {
    const emailPresent = availableProperties.has('email');
    const applicable = emailPresent && countConsentCheckboxesInTemplate() === 0;
    // Flag POSITIF (opt-in) : sa PRÉSENCE marque le form comme configuré avec le suivi ;
    // son ABSENCE = ancien form jamais configuré → le back NE trackera PAS (aucun suivi
    // par surprise). Défaut ON : quand le suivi est applicable et pas encore configuré, on
    // matérialise le flag à true (persisté au prochain enregistrement du modèle) — le toggle
    // reste coché par défaut, mais rien ne s'active tant que le form n'a pas été (ré)enregistré.
    let trackingEnabledFlag = (identifier
      ? getFormFieldConfig(identifier, 'tracking_consent_enabled')
      : undefined) as boolean | undefined;
    if (applicable && identifier && getFormByIdentifier(identifier) && trackingEnabledFlag === undefined) {
      updateFormFieldConfig(identifier, 'tracking_consent_enabled', true);
      trackingEnabledFlag = true;
    }
    consentStatusForTracking.trackingConsent = {
      applicable,
      enabled: applicable && trackingEnabledFlag !== false,
      disclosureCovered: getDisclosureChannelsFromTemplate().has('email'),
    };
  }

  // Sync actions_to_take config with evaluated state so the saved config stays accurate
  if (hasExistingConfig && identifier) {
    const newEnabledActions = result
      .filter((s) => s.isEnabled)
      .map((s) => s.action.id);
    const currentSaved = getFormFieldConfig(identifier, 'actions_to_take') || [];
    const currentSet = new Set(currentSaved as string[]);
    const newSet = new Set(newEnabledActions);

    if (currentSet.size !== newSet.size || [...currentSet].some((a) => !newSet.has(a))) {
      updateFormFieldConfig(identifier, 'actions_to_take', newEnabledActions);
    }
  }

  return result;
};

/**
 * Get the display name for a property
 * @param property - Property name
 * @returns Translated property name
 */
const NATIVE_PROPERTY_KEYS = ['email', 'first_name', 'last_name', 'birthday', 'phone_number', 'password', 'consent_email', 'consent_sms', 'consent_whatsapp'];

export const getPropertyDisplayName = (property: string): string => {
  // Check if it's a custom property (format: schemaId_propertyName)
  const customPropertyMatch = property.match(/^(\d+)_(.+)$/);
  if (customPropertyMatch) {
    return customPropertyMatch[2];
  }
  // Translate native properties via i18n
  if (NATIVE_PROPERTY_KEYS.includes(property)) {
    const t = (i18n.global as any).t;
    return t(`templateBuilder.formContactProperty.${property}`);
  }
  return property;
};

/**
 * Get all contact properties already used in the ENTIRE funnel
 * This includes properties from ALL templates in the funnel (before, current, and after)
 * @returns Set of property values that are already assigned to form fields
 */
export const getUsedPropertiesInFunnel = (): Set<string> => {
  const usedProperties = new Set<string>();

  if (!state.template) {
    return usedProperties;
  }

  // Check if this is a funnel template
  const isRootTemplate = state.template.configuration?.funnels;
  const isRedirectionTpl = state.template.configuration?.origin_template_id;

  if (!isRootTemplate && !isRedirectionTpl) {
    // Not a funnel - only check current template
    const currentFormFields = getFormFieldsSummary();
    currentFormFields.forEach((field) => {
      if (field.contactProperty) {
        usedProperties.add(field.contactProperty);
      }
    });
    return usedProperties;
  }

  // Get root template ID
  let rootTemplateId: number;
  if (isRootTemplate) {
    rootTemplateId = state.template.id;
  } else {
    rootTemplateId = parseInt(state.template.configuration.root_template_id as string, 10);
  }

  // Collect properties from root template
  const rootTemplate = state.templates.find((t) => t.id === rootTemplateId)
    || state.templatesLinkedToCampaign.find((t) => t.id === rootTemplateId);

  if (rootTemplate?.configuration?.forms) {
    Object.values(rootTemplate.configuration.forms).forEach((formConfig: any) => {
      const contactProperty = formConfig.configs?.contactProperty || formConfig.contactProperty;
      if (contactProperty) {
        usedProperties.add(contactProperty);
      }
    });
  }

  // Collect properties from all funnel templates
  if (state.templatesFromFunnel[rootTemplateId.toString()]) {
    state.templatesFromFunnel[rootTemplateId.toString()].forEach((funnelTemplate) => {
      if (funnelTemplate.configuration?.forms) {
        Object.values(funnelTemplate.configuration.forms).forEach((formConfig: any) => {
          const contactProperty = formConfig.configs?.contactProperty || formConfig.contactProperty;
          if (contactProperty) {
            usedProperties.add(contactProperty);
          }
        });
      }
    });
  }

  return usedProperties;
};
